import { useMutation, useQuery } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input, Label } from '../../components/ui/field';
import { ErrorState, LoadingState } from '../../components/ui/state';
import { api } from '../../shared/api';

export function StayPhotosPage() {
  const { stayId = '' } = useParams();
  const [message, setMessage] = useState<string>();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const stayQuery = useQuery({ queryKey: ['stay', stayId], queryFn: () => api.stays.get(stayId), enabled: Boolean(stayId) });
  const uploadPhotos = useMutation({
    mutationFn: async (files: File[]) => {
      if (files.length > 10) throw new Error('Puedes subir un maximo de 10 fotos a la vez.');
      if (files.some((file) => file.size > 10 * 1024 * 1024)) throw new Error('Cada foto debe pesar 10 MB o menos.');
      const uploaded = [];
      for (const file of files) {
        const upload = await api.files.createUpload({ fileName: file.name, contentType: file.type || 'application/octet-stream', sizeBytes: file.size, purpose: 'stay-photo', ownerId: stayId });
        await fetch(upload.uploadUrl, { method: 'PUT', headers: { 'content-type': file.type || 'application/octet-stream', 'if-none-match': '*' }, body: file });
        await api.stays.addPhoto(stayId, { fileId: upload.fileId, objectKey: upload.objectKey, fileName: file.name, contentType: file.type || 'application/octet-stream' });
        uploaded.push(file.name);
      }
      return uploaded;
    },
    onSuccess: (files) => {
      setSelectedFiles([]);
      setMessage(`${files.length} foto(s) cargada(s).`);
    },
  });
  if (stayQuery.isLoading) return <LoadingState label="Cargando hotel" />;
  if (stayQuery.isError) return <ErrorState message={stayQuery.error.message} />;
  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-slate-950">Fotos de hotel</h1>
        <p className="mt-1 text-sm text-slate-500">ID público: <span className="font-mono">{stayQuery.data?.shortId}</span></p>
      </div>
      <Card>
        <CardHeader><h2 className="text-sm font-semibold">Subir fotos</h2></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-1.5"><Label htmlFor="photos">Fotos</Label><Input id="photos" type="file" accept="image/*" multiple onChange={(event) => { setMessage(undefined); setSelectedFiles(Array.from(event.currentTarget.files ?? [])); }} /></div>
          {selectedFiles.length > 0 ? <p className="text-sm text-[#4f5d49]">{selectedFiles.length} foto(s) seleccionada(s): {selectedFiles.map((file) => file.name).join(', ')}</p> : null}
          {message ? <p className="text-sm text-[#596a52]">{message}</p> : null}
          {uploadPhotos.isError ? <p className="text-sm text-rose-600">{uploadPhotos.error.message}</p> : null}
          <Button type="button" disabled={selectedFiles.length === 0 || uploadPhotos.isPending} onClick={() => uploadPhotos.mutate(selectedFiles)}><Upload className="h-4 w-4" aria-hidden="true" />Confirmar subida</Button>
          <p className="text-xs text-slate-500">Comparte /gallery/{stayQuery.data?.shortId} con el cliente. Las fotos se eliminan al confirmar check-out.</p>
        </CardContent>
      </Card>
    </div>
  );
}
